import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldAlert } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO, { CANONICAL_ORIGIN } from "@/components/SEO";

/**
 * Shared layout for the six trust/legal pages.
 * Renders as concise, readable long-form content — NOT a marketing landing.
 */
export default function PolicyPage({
  slug,
  title,
  intro,
  metaTitle,
  metaDescription,
  sections = [],
  cta,
  disclaimer = true,
}) {
  const canonicalPath = `/${slug}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: metaTitle || title,
      description: metaDescription || intro,
      url: `${CANONICAL_ORIGIN}${canonicalPath}`,
      inLanguage: "tr-TR",
      isPartOf: { "@type": "WebSite", name: "Abonelik İptal", url: CANONICAL_ORIGIN },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: `${CANONICAL_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: title, item: `${CANONICAL_ORIGIN}${canonicalPath}` },
      ],
    },
  ];
  return (
    <div>
      <SEO
        title={metaTitle || `${title} | Abonelik İptal`}
        description={metaDescription || intro}
        path={canonicalPath}
        jsonLd={jsonLd}
      />
      <Header />

      <article className="max-w-3xl mx-auto px-5 md:px-8 pt-12 md:pt-16 pb-16">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#14A89F] mb-3">
          Abonelik İptal
        </div>
        <h1
          data-testid="policy-title"
          className="text-4xl md:text-5xl font-bold text-[#0F172A] tracking-tight"
        >
          {title}
        </h1>
        {intro && (
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            {intro}
          </p>
        )}

        {cta && <div className="mt-8">{cta}</div>}

        <div className="mt-10 space-y-10">
          {sections.map((s, i) => (
            <section key={i} data-testid={`policy-section-${i}`}>
              <h2 className="text-xl md:text-2xl font-semibold text-[#0F172A]">
                {s.heading}
              </h2>
              {s.body?.map((p, j) => (
                <p key={j} className="mt-3 text-slate-700 leading-relaxed">
                  {p}
                </p>
              ))}
              {s.bullets && (
                <ul className="mt-3 space-y-2 pl-1">
                  {s.bullets.map((b, k) => (
                    <li key={k} className="text-slate-700 leading-relaxed flex items-start gap-3">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#14A89F] shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.extra && <div className="mt-4">{s.extra}</div>}
            </section>
          ))}
        </div>

        {disclaimer && (
          <div className="mt-14 rounded-2xl bg-slate-50 border border-slate-200 p-5 flex items-start gap-3">
            <ShieldAlert size={16} className="text-[#E76A5A] mt-0.5 shrink-0" />
            <p className="text-sm text-slate-600 leading-relaxed">
              Abonelik İptal resmi kurum değildir ve hukuki danışmanlık vermez. Hazırlanan
              metinleri göndermeden önce siz kontrol edersiniz. Abonelik İptal, tüketici
              işlem süreçlerini kolaylaştıran yardımcı bir platformdur.
            </p>
          </div>
        )}

        <div className="mt-10 text-sm">
          <Link
            to="/dosya-baslat"
            data-testid="policy-cta"
            className="fs-btn-primary inline-flex items-center gap-2"
          >
            Başvuru Hazırla <ArrowRight size={16} />
          </Link>
        </div>
      </article>

      <Footer />
    </div>
  );
}
